import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, Check, Wifi } from "lucide-react";
import { IMAGES, scrollToId } from "@/lib/site";
import { Eyebrow } from "@/components/Primitives";

const EASE = [0.16, 1, 0.3, 1];
const LINES = [
  [{ t: "Keep more of" }],
  [{ t: "every " }, { t: "swipe,", serif: true }],
  [{ t: "tap & click." }],
];

const MaskLine = ({ parts, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.15 + i * 0.12, ease: EASE }}
    >
      {parts.map((p, k) => (
        <span key={k} className={p.serif ? "font-serif-i text-[var(--cobalt)] tracking-normal" : ""}>
          {p.t}
        </span>
      ))}
    </motion.span>
  </span>
);

const TiltVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, delay: 0.35, ease: EASE }}
      style={{ perspective: 1200 }}
      className="relative"
      data-testid="hero-visual"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ clipPath: "inset(14% 14% 14% 14% round 28px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
          transition={{ duration: 1.4, delay: 0.35, ease: EASE }}
          className="relative aspect-[4/5] rounded-[28px] overflow-hidden bg-[var(--ink)]"
        >
          <motion.img
            src={IMAGES.hero}
            alt="Customer paying with a card at a point-of-sale terminal"
            style={{ y: imgY, scale: 1.18 }}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_35%,transparent_0%,transparent_35%,rgba(11,18,32,0.75)_100%)]" />
        </motion.div>
        <motion.div
          style={{ transform: "translateZ(60px)" }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8, ease: EASE }}
          className="absolute -left-4 sm:-left-10 bottom-10 rounded-2xl bg-white/90 backdrop-blur-xl shadow-2xl px-5 py-4 flex items-center gap-4"
          data-testid="hero-approved-chip"
        >
          <span className="relative grid place-items-center h-11 w-11 rounded-full bg-[var(--mint)] text-white">
            <span className="pulse-ring absolute inset-0 rounded-full bg-[var(--mint)]" />
            <Check className="relative h-5 w-5" />
          </span>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-600">Payment approved</p>
            <p className="font-display text-xl font-semibold">$48.20</p>
          </div>
        </motion.div>
        <motion.div
          style={{ transform: "translateZ(40px)" }}
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8, ease: EASE }}
          className="absolute right-4 top-4 sm:-right-6 sm:top-10 rounded-full bg-[var(--ink)] text-white px-4 py-2 flex items-center gap-2 text-sm font-medium shadow-xl"
        >
          <Wifi className="h-4 w-4 rotate-90 text-[var(--mint)]" /> Tap · Dip · Swipe · Online
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => (
  <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 grid-lines [mask-image:radial-gradient(ellipse_at_top_left,black_20%,transparent_70%)]" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
      <div className="lg:col-span-7">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          <Eyebrow>Merchant services · POS · Payroll</Eyebrow>
        </motion.div>
        <h1
          className="mt-6 font-display font-semibold tracking-[-0.035em] leading-[0.95] text-[3.1rem] sm:text-7xl lg:text-[6.2rem]"
          data-testid="hero-headline"
        >
          {LINES.map((parts, i) => (
            <MaskLine key={i} parts={parts} i={i} />
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.9, ease: EASE }}
          className="mt-8 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
        >
          Transparent card processing for retail counters, online stores and businesses on the move — plus payroll,
          handled by the same team. Get a free statement review and see exactly what you could save.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-col sm:flex-row gap-3"
        >
          <button
            onClick={() => scrollToId("quote")}
            data-testid="hero-cta-quote"
            className="group inline-flex items-center justify-center gap-3 rounded-full bg-[var(--cobalt)] text-white px-7 py-4 font-semibold hover:bg-[var(--ink)] transition-colors"
          >
            Get my free quote
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={() => scrollToId("calculator")}
            data-testid="hero-cta-calculator"
            className="inline-flex items-center justify-center rounded-full border border-slate-300 px-7 py-4 font-semibold hover:border-[var(--ink)] hover:bg-white transition-colors"
          >
            Calculate my savings
          </button>
        </motion.div>
        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600"
        >
          {["No-obligation quote", "Free statement review", "Response within 48 hours"].map((t) => (
            <li key={t} className="flex items-center gap-2">
              <Check className="h-4 w-4 text-[var(--mint)]" /> {t}
            </li>
          ))}
        </motion.ul>
      </div>
      <div className="lg:col-span-5 px-4 sm:px-8 lg:px-0">
        <TiltVisual />
      </div>
    </div>
  </section>
);
